/**
 * Asset Loader - GLTF and texture loading
 *
 * Features:
 * - GLTF loading with GLTFLoader
 * - Draco compression support
 * - Asset caching
 * - Quality-based model path selection
 */

import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader.js';

export type AssetQuality = 'low' | 'medium' | 'high';

export interface AssetLoaderConfig {
  quality?: AssetQuality;
  basePath?: string;
  dracoPath?: string;
  enableCache?: boolean;
}

export class AssetLoader {
  private gltfLoader: GLTFLoader;
  private textureLoader: THREE.TextureLoader;
  private dracoLoader: DRACOLoader;
  private quality: AssetQuality;
  private basePath: string;
  private enableCache: boolean;

  // Cache maps
  private gltfCache: Map<string, GLTF> = new Map();
  private objCache: Map<string, THREE.Group> = new Map();
  private textureCache: Map<string, THREE.Texture> = new Map();

  // Loading manager for progress tracking
  private loadingManager: THREE.LoadingManager;
  private loadProgress = { loaded: 0, total: 0 };

  constructor(config: AssetLoaderConfig = {}) {
    this.quality = config.quality ?? 'medium';
    this.basePath = config.basePath ?? '/assets';
    this.enableCache = config.enableCache ?? true;

    // Setup loading manager
    this.loadingManager = new THREE.LoadingManager();
    this.loadingManager.onProgress = (_url, loaded, total) => {
      this.loadProgress = { loaded, total };
    };

    // Setup texture loader
    this.textureLoader = new THREE.TextureLoader(this.loadingManager);

    // Setup Draco loader for compressed models
    this.dracoLoader = new DRACOLoader(this.loadingManager);
    const dracoPath = config.dracoPath ?? 'https://www.gstatic.com/draco/versioned/decoders/1.5.6/';
    this.dracoLoader.setDecoderPath(dracoPath);

    // Setup GLTF loader with Draco support
    this.gltfLoader = new GLTFLoader(this.loadingManager);
    this.gltfLoader.setDRACOLoader(this.dracoLoader);

  }

  /**
   * Load a GLTF model
   * Automatically selects quality-based path if quality suffix provided
   */
  async loadGLTF(
    path: string,
    useQualitySuffix = false
  ): Promise<GLTF> {
    const fullPath = this.resolveGLTFPath(path, useQualitySuffix);

    // Check cache
    if (this.enableCache && this.gltfCache.has(fullPath)) {
      return this.gltfCache.get(fullPath)!;
    }

    // Load asset
    return new Promise((resolve, reject) => {
      this.gltfLoader.load(
        fullPath,
        (gltf) => {
          if (this.enableCache) {
            this.gltfCache.set(fullPath, gltf);
          }
          resolve(gltf);
        },
        undefined,
        (error) => {
          console.error(`Failed to load GLTF: ${fullPath}`, error);
          reject(error);
        }
      );
    });
  }

  /**
   * Load an OBJ model with MTL materials
   * @param objPath - Path to .obj file
   * @param mtlPath - Optional path to .mtl file (if not provided, assumes same name as obj)
   */
  async loadOBJ(
    objPath: string,
    mtlPath?: string
  ): Promise<THREE.Group> {
    const fullObjPath = objPath.startsWith('/') ? objPath : `${this.basePath}/${objPath}`;

    // Check cache
    if (this.enableCache && this.objCache.has(fullObjPath)) {
      return this.objCache.get(fullObjPath)!.clone();
    }

    try {
      // Determine MTL path
      const fullMtlPath = mtlPath
        ? (mtlPath.startsWith('/') ? mtlPath : `${this.basePath}/${mtlPath}`)
        : fullObjPath.replace('.obj', '.mtl');

      // Extract directory for texture loading (everything before the filename)
      const mtlDir = fullMtlPath.substring(0, fullMtlPath.lastIndexOf('/') + 1);


      // Create a fresh MTL loader for this load (to avoid path conflicts)
      const mtlLoader = new MTLLoader(this.loadingManager);
      mtlLoader.setResourcePath(mtlDir); // Set resource path for textures
      mtlLoader.setPath(mtlDir); // Set base path for MTL file

      // Load MTL first
      const materials = await new Promise<any>((resolve, _reject) => {
        mtlLoader.load(
          fullMtlPath,
          (mtl) => {
            mtl.preload();
            resolve(mtl);
          },
          undefined,
          (error) => {
            console.warn(`⚠️ MTL file not found or failed to load: ${fullMtlPath}`, error);
            resolve(null); // Continue without materials
          }
        );
      });

      // Load OBJ with materials
      const group = await new Promise<THREE.Group>((resolve, reject) => {
        const objLoader = new OBJLoader(this.loadingManager);

        if (materials) {
          objLoader.setMaterials(materials);
        }

        objLoader.load(
          fullObjPath,
          (obj) => {
            if (this.enableCache) {
              this.objCache.set(fullObjPath, obj);
            }
            resolve(obj);
          },
          undefined,
          (error) => {
            console.error(`❌ Failed to load OBJ: ${fullObjPath}`, error);
            reject(error);
          }
        );
      });

      return group;
    } catch (error) {
      console.error(`❌ Failed to load OBJ model: ${objPath}`, error);
      throw error;
    }
  }

  /**
   * Load a texture
   */
  async loadTexture(
    path: string,
    useQualitySuffix = false
  ): Promise<THREE.Texture> {
    const fullPath = this.resolveTexturePath(path, useQualitySuffix);

    // Check cache
    if (this.enableCache && this.textureCache.has(fullPath)) {
      return this.textureCache.get(fullPath)!;
    }

    // Load texture
    return new Promise((resolve, reject) => {
      this.textureLoader.load(
        fullPath,
        (texture) => {
          if (this.enableCache) {
            this.textureCache.set(fullPath, texture);
          }
          resolve(texture);
        },
        undefined,
        (error) => {
          console.error(`Failed to load texture: ${fullPath}`, error);
          reject(error);
        }
      );
    });
  }

  /**
   * Resolve GLTF path with quality suffix
   * e.g., "models/wallet.glb" -> "models/wallet-medium.glb"
   */
  private resolveGLTFPath(path: string, useQualitySuffix: boolean): string {
    const fullPath = path.startsWith('/') ? path : `${this.basePath}/${path}`;

    if (!useQualitySuffix) {
      return fullPath;
    }

    // Insert quality before extension
    // "models/wallet.glb" -> "models/wallet-medium.glb"
    const parts = fullPath.split('.');
    const extension = parts.pop();
    const baseName = parts.join('.');
    return `${baseName}-${this.quality}.${extension}`;
  }

  /**
   * Resolve texture path with quality suffix
   * e.g., "textures/bill_100.png" -> "textures/bill_100_medium.png"
   */
  private resolveTexturePath(path: string, useQualitySuffix: boolean): string {
    const fullPath = path.startsWith('/') ? path : `${this.basePath}/${path}`;

    if (!useQualitySuffix) {
      return fullPath;
    }

    // Insert quality before extension
    const parts = fullPath.split('.');
    const extension = parts.pop();
    const baseName = parts.join('.');
    return `${baseName}_${this.quality}.${extension}`;
  }

  /**
   * Get loading progress
   */
  getProgress(): { loaded: number; total: number; percent: number } {
    const percent = this.loadProgress.total > 0
      ? (this.loadProgress.loaded / this.loadProgress.total) * 100
      : 0;
    return { ...this.loadProgress, percent };
  }

  /**
   * Set quality level (affects future loads)
   */
  setQuality(quality: AssetQuality): void {
    this.quality = quality;
  }

  /**
   * Get current quality
   */
  getQuality(): AssetQuality {
    return this.quality;
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    // Dispose textures
    for (const texture of this.textureCache.values()) {
      texture.dispose();
    }
    this.textureCache.clear();

    // Clear GLTF cache (note: doesn't dispose geometries/materials)
    this.gltfCache.clear();

    // Clear OBJ cache
    this.objCache.clear();
  }

  /**
   * Dispose loader and all cached assets
   */
  dispose(): void {
    this.clearCache();
    this.dracoLoader.dispose();
  }
}
